import React from "react";
import { fmt, upColor, upBgClass } from "@/lib/format";
import { TID } from "@/lib/testIds";
import { ArrowUp, ArrowDown, Building2, Users, Calendar, MapPin, Briefcase, Radio, CircleAlert } from "lucide-react";

function DataSourceBadge({ source, status, updatedAt, tradeDate }) {
  if (!source) return null;
  const label = {
    finnhub: "Finnhub · Live",
    "finnhub+curated": "Finnhub · Curated",
    twse: "TWSE · Live",
    "twse+curated": "TWSE · Curated",
    "twse+finmind": "TWSE + FinMind · Live",
    "twse+finmind+curated": "TWSE + FinMind · Curated",
    tpex: "TPEx · Live",
    "tpex+curated": "TPEx · Curated",
    "tpex+finmind": "TPEx + FinMind · Live",
    "tpex+finmind+curated": "TPEx + FinMind · Curated",
    mock: "Mock Data · 情境展示",
  }[source] || source;
  const isLive = source !== "mock";
  const isRateLimited = status === "rate_limited";
  const bg = isLive
    ? "bg-emerald-500/10 border-emerald-500/25 text-emerald-300"
    : "bg-amber-500/10 border-amber-500/25 text-amber-300";
  let d = "";
  try {
    if (updatedAt) {
      const dt = new Date(updatedAt);
      d = dt.toLocaleString("zh-Hant", { month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" });
    }
  } catch {
    /* ignore */
  }
  return (
    <div className="mt-2 flex items-center justify-end gap-2 flex-wrap">
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md border text-[10px] font-mono uppercase tracking-widest ${bg}`}>
        {isLive ? <Radio className="w-3 h-3" strokeWidth={2} /> : <CircleAlert className="w-3 h-3" strokeWidth={2} />}
        {label}
      </span>
      {isRateLimited && (
        <span className="text-[10px] font-mono text-amber-400/80">
          Finnhub 額度暫時受限，暫用 mock
        </span>
      )}
      {tradeDate && (
        <span className="text-[10px] font-mono text-zinc-500">
          最後交易日 {tradeDate}
        </span>
      )}
      {d && !tradeDate && <span className="text-[10px] font-mono text-zinc-500">更新 {d}</span>}
    </div>
  );
}

function Metric({ label, value, sub, testid }) {
  return (
    <div className="card-tactical p-4 sm:p-5" data-testid={testid}>
      <div className="text-[10px] sm:text-xs font-medium text-zinc-500 uppercase tracking-widest">
        {label}
      </div>
      <div className="mt-2 metric-value text-xl sm:text-2xl font-bold text-white">
        {value}
      </div>
      {sub && <div className="mt-1 text-xs text-zinc-500">{sub}</div>}
    </div>
  );
}

export default function StockOverview({ data }) {
  const up = data.change_pct >= 0;
  const currency = data.currency || "USD";

  return (
    <section data-testid={TID.stockOverviewCard} className="fade-in">
      {/* Ticker + Price row */}
      <div className="card-tactical p-5 sm:p-6 lg:p-7">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-5">
          <div className="min-w-0">
            <div className="flex items-center gap-2.5 flex-wrap">
              <span className="text-[10px] uppercase tracking-widest font-mono text-blue-400 bg-blue-500/10 border border-blue-500/20 px-2 py-0.5 rounded">
                {data.exchange}
              </span>
              <span className="text-[10px] uppercase tracking-widest font-mono text-zinc-500 bg-zinc-800 border border-zinc-700 px-2 py-0.5 rounded">
                {data.industry}
              </span>
              <span className="text-[10px] uppercase tracking-widest font-mono text-zinc-500">
                {currency}
              </span>
            </div>
            <div className="mt-3 flex items-baseline gap-3 flex-wrap">
              <h2 className="text-2xl sm:text-3xl font-black tracking-tight text-white">
                {data.name_zh || data.name}
              </h2>
              <span className="font-mono text-lg sm:text-xl font-bold text-zinc-400 tracking-tight">
                {data.ticker}
              </span>
            </div>
            <p className="mt-2 text-xs sm:text-sm text-zinc-500 max-w-2xl leading-relaxed line-clamp-2">
              {data.summary}
            </p>
          </div>

          <div className="lg:text-right">
            <div className="metric-value text-4xl sm:text-5xl font-black text-white tracking-tighter">
              {fmt.price(data.price, currency)}
            </div>
            <div
              className={`mt-2 inline-flex items-center gap-1.5 px-2 py-1 rounded-md border font-mono text-sm ${upBgClass(
                data.change_pct
              )}`}
            >
              {up ? (
                <ArrowUp className="w-3.5 h-3.5" strokeWidth={2.4} />
              ) : (
                <ArrowDown className="w-3.5 h-3.5" strokeWidth={2.4} />
              )}
              <span className="font-bold">{fmt.pct(data.change_pct)}</span>
              <span className="text-xs opacity-70">
                ({up ? "+" : ""}
                {fmt.num(data.change_abs)})
              </span>
            </div>
            <DataSourceBadge
              source={data.data_source}
              status={data.av_status}
              updatedAt={data.last_updated}
              tradeDate={data.twse_last_trade_date}
            />
          </div>
        </div>

        {/* Bento metrics */}
        <div className="mt-6 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 sm:gap-4">
          <Metric label="市值" value={fmt.bigMoney(data.market_cap, currency)} />
          <Metric label="本益比 P/E" value={fmt.num(data.pe, 1)} />
          <Metric label="EPS" value={fmt.num(data.eps)} sub={`每股盈餘`} />
          <Metric label="ROE" value={`${fmt.num(data.roe, 1)}%`} />
          <Metric label="ROA" value={`${fmt.num(data.roa, 1)}%`} />
          <Metric label="負債比" value={`${fmt.num(data.debt_ratio, 1)}%`} />
        </div>
      </div>

      {/* Company profile */}
      <div className="mt-4 grid grid-cols-1 lg:grid-cols-12 gap-4">
        <div className="lg:col-span-8 card-tactical p-5 sm:p-6" data-testid={TID.companyProfile}>
          <div className="text-[11px] uppercase tracking-widest font-semibold text-zinc-500 mb-3">
            公司概況 · Company Profile
          </div>
          <p className="text-sm text-zinc-300 leading-relaxed">{data.summary}</p>

          <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-4">
            <ProfileItem icon={Calendar} label="成立" value={data.founded} />
            <ProfileItem icon={MapPin} label="總部" value={data.hq} />
            <ProfileItem icon={Users} label="員工" value={fmt.int(data.employees)} />
            <ProfileItem icon={Briefcase} label="CEO" value={data.ceo} />
          </div>

          <div className="mt-6">
            <div className="text-[11px] uppercase tracking-widest font-semibold text-zinc-500 mb-2">
              主要產品 / 服務
            </div>
            <div className="flex flex-wrap gap-2">
              {(data.products || []).map((p) => (
                <span
                  key={p}
                  className="px-2.5 py-1 rounded-md border border-zinc-800 bg-zinc-900/60 text-xs text-zinc-300"
                >
                  {p}
                </span>
              ))}
            </div>
          </div>

          <div className="mt-6">
            <div className="text-[11px] uppercase tracking-widest font-semibold text-zinc-500 mb-2">
              商業模式
            </div>
            <p className="text-sm text-zinc-300 leading-relaxed">{data.business_model}</p>
          </div>
        </div>

        <div className="lg:col-span-4 card-tactical p-5 sm:p-6">
          <div className="text-[11px] uppercase tracking-widest font-semibold text-zinc-500 mb-3">
            <span className="inline-flex items-center gap-1.5">
              <Building2 className="w-3.5 h-3.5" strokeWidth={1.6} />
              營收結構
            </span>
          </div>
          <div className="space-y-3">
            {(data.revenue_sources || []).map((s) => (
              <div key={s.name}>
                <div className="flex items-baseline justify-between mb-1">
                  <span className="text-xs text-zinc-300">{s.name}</span>
                  <span className="font-mono text-xs text-zinc-400">{s.pct.toFixed(1)}%</span>
                </div>
                <div className="h-1.5 rounded-full bg-zinc-800 overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-blue-500 to-cyan-400"
                    style={{ width: `${Math.min(100, s.pct)}%` }}
                  />
                </div>
              </div>
            ))}
          </div>

          <div className="mt-5 pt-4 border-t border-zinc-800">
            <div className="text-[11px] uppercase tracking-widest font-semibold text-zinc-500 mb-2">
              主要市場
            </div>
            <div className="flex flex-wrap gap-1.5">
              {(data.markets || []).map((m) => (
                <span
                  key={m}
                  className="px-2 py-0.5 rounded border border-zinc-800 bg-zinc-900 text-[11px] text-zinc-300"
                >
                  {m}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function ProfileItem({ icon: Icon, label, value }) {
  return (
    <div>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-zinc-500">
        <Icon className="w-3 h-3" strokeWidth={1.6} />
        {label}
      </div>
      <div className="mt-1 text-sm text-zinc-100 font-medium truncate">{value}</div>
    </div>
  );
}
